import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './abSwiper.style.scss';

/**
 * `<ab-swiper>` — Web Component wrapper attorno a Swiper.js
 *
 * Gestisce automaticamente l'inizializzazione e la distruzione dell'istanza Swiper
 * al mount/unmount del componente nel DOM.
 *
 * ---
 * ## Struttura HTML richiesta
 *
 * Il componente si aspetta obbligatoriamente un `.swiper` come figlio diretto.
 * I pulsanti di navigazione e la paginazione sono opzionali.
 *
 * ```html
 * <ab-swiper slides-per-view="2" space-between="16">
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">Slide 1</div>
 *       <div class="swiper-slide">Slide 2</div>
 *     </div>
 *     <!-- opzionali -->
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *     <div class="swiper-pagination"></div>
 *   </div>
 * </ab-swiper>
 * ```
 *
 * ---
 * ## Attributi
 *
 * | Attributo        | Tipo                  | Default | Descrizione                                                                 |
 * |------------------|-----------------------|---------|-----------------------------------------------------------------------------|
 * | `slides-per-view`| `number \| "auto"`    | `1`     | Numero di slide visibili. Accetta valori decimali (es. `1.75`).             |
 * | `space-between`  | `number`              | `0`     | Gap in px tra le slide.                                                     |
 * | `loop`           | attributo booleano    | `false` | Se presente, abilita la navigazione ciclica infinita.                       |
 * | `autoplay`       | `number`              | —       | Se presente, abilita l'autoplay. Il valore è il delay in ms (default 3000). |
 * | `free-mode`      | attributo booleano    | `false` | Se presente, abilita il free scroll (senza snap alle slide).                |
 * | `breakpoints`    | JSON stringificato    | —       | Oggetto breakpoints di Swiper. La chiave è la larghezza in px.              |
 *
 * ---
 * ## Esempi
 *
 * ### Slider semplice con navigazione
 * ```html
 * <ab-swiper slides-per-view="1" loop>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">A</div>
 *       <div class="swiper-slide">B</div>
 *     </div>
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *   </div>
 * </ab-swiper>
 * ```
 *
 * ### Autoplay con paginazione
 * ```html
 * <ab-swiper slides-per-view="1" autoplay="4000" loop>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">...</div>
 *     <div class="swiper-pagination"></div>
 *   </div>
 * </ab-swiper>
 * ```
 *
 * ### Responsive con breakpoints
 * ```html
 * <ab-swiper
 *   slides-per-view="1.25"
 *   space-between="16"
 *   breakpoints='{"640":{"slidesPerView":2},"1024":{"slidesPerView":3}}'>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">...</div>
 *   </div>
 * </ab-swiper>
 * ```
 *
 * ### Creazione programmatica (es. slide dinamiche da API)
 * ```ts
 * const abSwiper = createDomElementHandler('ab-swiper');
 * abSwiper.setAttribute('slides-per-view', '1.75');
 * abSwiper.setAttribute('space-between', '16');
 * abSwiper.setAttribute('loop', '');
 *
 * const container = createDomElementHandler('div', 'swiper');
 * const wrapper = createDomElementHandler('div', 'swiper-wrapper');
 * products.forEach(p => {
 *   const slide = createDomElementHandler('div', 'swiper-slide');
 *   // ... popola la slide
 *   wrapper.appendChild(slide);
 * });
 * container.appendChild(wrapper);
 * abSwiper.appendChild(container);
 *
 * document.querySelector('#host').replaceChildren(abSwiper);
 * ```
 *
 * ---
 * ## Note
 * - L'istanza Swiper viene distrutta automaticamente quando il componente viene rimosso dal DOM.
 * - I moduli attivi sono: Navigation, Pagination, Autoplay, FreeMode.
 * - Per slide di altezza uniforme quando il contenuto varia, impostare un'altezza fissa
 *   sul `.swiper` oppure usare `height: auto !important` sulle `.swiper-slide` via CSS.
 */
export function defineAbSwiperElement() {
  const TAG_NAME = 'ab-swiper';
  if (customElements.get(TAG_NAME)) return;

  class AbSwiperElement extends HTMLElement {
    #swiper?: Swiper;
    #initFrame?: number;

    connectedCallback() {
      this.#initFrame = requestAnimationFrame(() => {
        this.#initFrame = undefined;
        if (this.isConnected) this.#init();
      });
    }

    disconnectedCallback() {
      if (this.#initFrame !== undefined) {
        cancelAnimationFrame(this.#initFrame);
        this.#initFrame = undefined;
      }
      this.#swiper?.destroy(true, true);
      this.#swiper = undefined;
    }

    #init() {
      const container = this.querySelector<HTMLElement>('.swiper');
      if (!container) return;

      const slidesPerView = this.getAttribute('slides-per-view');
      const spaceBetween = Number(this.getAttribute('space-between') ?? 0);
      const autoplayDelay = this.getAttribute('autoplay');
      const loop = this.hasAttribute('loop');
      const freeMode = this.hasAttribute('free-mode');
      const breakpoints = this.#parseBreakpoints();

      this.#swiper = new Swiper(container, {
        modules: [Navigation, Pagination, Autoplay, FreeMode],
        loop,
        freeMode,
        slidesPerView: slidesPerView === 'auto' ? 'auto' : Number(slidesPerView ?? 1),
        spaceBetween,
        autoplay: autoplayDelay != null
          ? { delay: Number(autoplayDelay) || 3000, disableOnInteraction: false }
          : false,
        navigation: {
          nextEl: this.querySelector<HTMLElement>('.swiper-button-next'),
          prevEl: this.querySelector<HTMLElement>('.swiper-button-prev'),
        },
        pagination: {
          el: this.querySelector<HTMLElement>('.swiper-pagination'),
          clickable: true,
        },
        ...(Object.keys(breakpoints).length ? { breakpoints } : {}),
      });
    }

    #parseBreakpoints(): Record<number, object> {
      const raw = this.getAttribute('breakpoints');
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
  }

  customElements.define(TAG_NAME, AbSwiperElement);
}
